import { useEffect, useRef, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  PERSONAL_DATA_PAGE_SIZE,
  PERSONAL_DATA_ROLE_OPTIONS,
  REFRESH_INTERVAL_MS,
  buildPersonalDataExportParams,
  buildPersonalDataPageParams,
  formatDateTime,
  formatReportAmount,
} from './personalDataReportConstants';

const EMPTY_FILTERS = {
  roleId: '1',
  topInviterId: '',
  inviterId: '',
  memberId: '',
  totalBalanceMin: '',
  totalBalanceMax: '',
  totalOrderPriceMin: '',
  totalOrderPriceMax: '',
  totalDepositMin: '',
  totalDepositMax: '',
  noDepositDay: '',
  noLoginDay: '',
};

const TABLE_COLUMNS = [
  '頂級上級用戶 ID',
  '直屬上級用戶 ID',
  '用戶 ID',
  '可取款金額',
  '下單總額',
  '下單手續費總額',
  '盈虧總額',
  '充值總額',
  '首存金額',
  '提現總額',
  '系統加扣款',
  '最後登入時間',
  '最後充值時間',
];

export default function PersonalDataReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const filtersRef = useRef(filters);
  const pageRef = useRef(page);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.memberDailyStatisticalPageQuery(
        buildPersonalDataPageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total || 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    const timer = setInterval(() => {
      loadRecords(pageRef.current, filtersRef.current);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1);
  }

  function handleReset() {
    setFilters(EMPTY_FILTERS);
    setError('');
    loadRecords(1, EMPTY_FILTERS);
  }

  async function handleExport(exportCurrentPageOnly) {
    setExporting(true);
    setError('');
    try {
      await adminApi.exportMemberDailyStatisticalExcel(
        buildPersonalDataExportParams(filters, exportCurrentPageOnly, page)
      );
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  function renderRow(row) {
    return (
      <tr key={row.memberId}>
        <td>{row.topInviterId ?? '—'}</td>
        <td>{row.inviterId ?? '—'}</td>
        <td>{row.memberId ?? '—'}</td>
        <td>{formatReportAmount(row.totalBalance)}</td>
        <td>{formatReportAmount(row.totalOrderPrice)}</td>
        <td>{formatReportAmount(row.totalOrderFeePrice)}</td>
        <td>{formatReportAmount(row.totalProfit)}</td>
        <td>{formatReportAmount(row.totalDeposit)}</td>
        <td>{formatReportAmount(row.firstDepositAmount)}</td>
        <td>{formatReportAmount(row.totalWithdrawal)}</td>
        <td>{formatReportAmount(row.systemModifyAmount)}</td>
        <td>{formatDateTime(row.lastLoginTime)}</td>
        <td>{formatDateTime(row.lastDepositTime)}</td>
      </tr>
    );
  }

  return (
    <div className="page personal-data-report-page">
      <div className="section-header">
        <h2>個人數據報表</h2>
        <p className="page-hint page-hint--warn">每 5 分鐘自動更新頁面</p>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="personalRoleId">帳號類型</label>
          <select
            id="personalRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {PERSONAL_DATA_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="personalTopInviterId">頂級上級用戶 ID</label>
          <input
            id="personalTopInviterId"
            type="text"
            placeholder="請輸入頂級上級用戶 ID"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="personalInviterId">直屬上級用戶 ID</label>
          <input
            id="personalInviterId"
            type="text"
            placeholder="請輸入直屬上級用戶 ID"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="personalMemberId">用戶 ID</label>
          <input
            id="personalMemberId"
            type="text"
            placeholder="請輸入用戶 ID"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field form-field--range">
          <label htmlFor="personalBalanceMin">可取款金額</label>
          <div className="range-inputs">
            <input
              id="personalBalanceMin"
              type="text"
              placeholder="最小"
              value={filters.totalBalanceMin}
              onChange={(e) => updateFilter('totalBalanceMin', e.target.value)}
            />
            <span>—</span>
            <input
              type="text"
              placeholder="最大"
              value={filters.totalBalanceMax}
              onChange={(e) => updateFilter('totalBalanceMax', e.target.value)}
            />
          </div>
        </div>

        <div className="form-field form-field--range">
          <label htmlFor="personalOrderMin">下單總額</label>
          <div className="range-inputs">
            <input
              id="personalOrderMin"
              type="text"
              placeholder="最小"
              value={filters.totalOrderPriceMin}
              onChange={(e) => updateFilter('totalOrderPriceMin', e.target.value)}
            />
            <span>—</span>
            <input
              type="text"
              placeholder="最大"
              value={filters.totalOrderPriceMax}
              onChange={(e) => updateFilter('totalOrderPriceMax', e.target.value)}
            />
          </div>
        </div>

        <div className="form-field form-field--range">
          <label htmlFor="personalDepositMin">充值金額</label>
          <div className="range-inputs">
            <input
              id="personalDepositMin"
              type="text"
              placeholder="最小"
              value={filters.totalDepositMin}
              onChange={(e) => updateFilter('totalDepositMin', e.target.value)}
            />
            <span>—</span>
            <input
              type="text"
              placeholder="最大"
              value={filters.totalDepositMax}
              onChange={(e) => updateFilter('totalDepositMax', e.target.value)}
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="personalNoDepositDay">最近幾天沒充值</label>
          <input
            id="personalNoDepositDay"
            type="number"
            min="0"
            placeholder="天數"
            value={filters.noDepositDay}
            onChange={(e) => updateFilter('noDepositDay', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="personalNoLoginDay">最近幾天沒登入</label>
          <input
            id="personalNoLoginDay"
            type="number"
            min="0"
            placeholder="天數"
            value={filters.noLoginDay}
            onChange={(e) => updateFilter('noLoginDay', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出Excel（全部資料）'}
          </button>
        </div>
      </form>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="page-status">載入中…</p> : null}

      <div className="table-wrap table-wrap--wide">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  暫無資料
                </td>
              </tr>
            ) : (
              records.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PERSONAL_DATA_PAGE_SIZE}
        onPageChange={(nextPage) => loadRecords(nextPage)}
      />
    </div>
  );
}
